import { useRef } from 'react';
import { C, soft } from '@parsina/framework/tokens';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// ─────────────────────────────────────────────────────────────────────────────
// Accent preset picker.
//
// One of the two components shared by both surfaces — the swatches are the same
// size and behaviour on a phone as on a workstation (44px targets are the
// mobile floor and do no harm on desktop), so forking it would be duplication
// rather than adaptation.
//
// Accessibility: a real radiogroup. Arrow keys move between options and select
// (WAI-ARIA radio pattern), only the selected option is in the tab order, and
// the selected state is carried by a checkmark and a ring, never by colour
// alone (§12.4) — a doctor with a colour-vision deficiency has to be able to
// tell which accent is active.
// ─────────────────────────────────────────────────────────────────────────────

export default function ThemePicker({ presets, value, onChange, testId = 'settings-theme' }) {
  const { t, isRTL } = useTranslation();
  const refs = useRef({});

  const onKeyDown = (e) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();

    const i = presets.findIndex((p) => p.id === value);
    const current = i === -1 ? 0 : i;
    // The arrows follow what the doctor SEES, so which one advances depends on
    // the direction the grid is laid out in — not on the array order.
    const forward = isRTL
      ? (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
      : (e.key === 'ArrowRight' || e.key === 'ArrowDown');
    const step = forward ? 1 : -1;

    let next;
    if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = presets.length - 1;
    else next = (current + step + presets.length) % presets.length;

    const id = presets[next].id;
    onChange(id);
    refs.current[id]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('settings.appearance.themePickerLabel')}
      data-testid={testId}
      onKeyDown={onKeyDown}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
        gap: 12,
      }}
    >
      {presets.map((preset) => {
        const selected = preset.id === value;
        return (
          <button
            key={preset.id}
            ref={(el) => { refs.current[preset.id] = el; }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            data-testid={`${testId}-option-${preset.id}`}
            onClick={() => onChange(preset.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              minHeight: 44, padding: '10px 12px',
              borderRadius: 10, cursor: 'pointer', textAlign: 'start',
              background: selected ? soft(C.navy, 0.06) : C.card,
              border: `1.5px solid ${selected ? C.navy : C.border}`,
              boxShadow: selected ? `0 0 0 3px ${soft(C.navy, 0.13)}` : 'none',
              transition: 'border-color .15s, box-shadow .15s, background .15s',
            }}
          >
            <span
              aria-hidden
              style={{
                width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${preset.swatch2}, ${preset.swatch})`,
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            />
            <span style={{
              flex: 1, minWidth: 0, fontSize: 13,
              fontWeight: selected ? 700 : 600,
              color: selected ? C.textPrimary : C.textSecondary,
            }}>
              {preset.label}
            </span>
            {/* Redundant with the ring on purpose — colour is never the only
                carrier of the selected state. */}
            {selected && (
              <svg viewBox="0 0 24 24" width={16} height={16} fill={C.navy} aria-hidden style={{ flexShrink: 0 }}>
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );
}
